import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export interface GuardrailUsage {
  id: string;
  definitionId: string;
  scope: 'global' | 'capability';
  capabilityId: string | null;
  baselineValue: number | null;
  multiplierFactor: number | null;
  multiplierStep: number | null;
  multiplierLabel: string | null;
  optionFactors: Record<string, number> | null;
  defaultValue: string | null;
  sortOrder: number;
  isActive: boolean;
  // Joined from definition
  code: string;
  name: string;
  description: string | null;
  inputType: 'number' | 'select' | 'boolean';
  options: string[] | null;
  minValue: number | null;
  maxValue: number | null;
  // Activity links
  linkedActivityIds: string[];
}

export function useGuardrailUsages(scope: 'global' | 'capability', capabilityId?: string | null) {
  return useQuery({
    queryKey: ['guardrail-usages', scope, capabilityId],
    queryFn: () => api.get<{ usages: GuardrailUsage[] }>('/vcf/guardrail-usages', {
      scope,
      capabilityId: capabilityId || undefined,
    }),
    enabled: scope === 'global' || !!capabilityId,
  });
}

export function useAddGuardrailUsage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      definitionId: string;
      scope: 'global' | 'capability';
      capabilityId?: string;
      baselineValue?: number;
      multiplierFactor?: number;
      multiplierStep?: number;
      multiplierLabel?: string;
      optionFactors?: Record<string, number>;
      defaultValue?: string;
      linkedActivityIds?: string[];
    }) => api.post('/vcf/guardrail-usages', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardrail-usages'] });
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

export function useUpdateGuardrailUsage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string;
      baselineValue?: number;
      multiplierFactor?: number;
      multiplierStep?: number;
      multiplierLabel?: string;
      optionFactors?: Record<string, number>;
      defaultValue?: string;
      linkedActivityIds?: string[];
    }) => api.patch(`/vcf/guardrail-usages/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardrail-usages'] });
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

export function useRemoveGuardrailUsage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/vcf/guardrail-usages/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardrail-usages'] });
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}
